import { z } from 'zod'

export const createLatinCharacterRegex = (allowedSymbols?: string[]): RegExp => {
  const defaultSymbols = " \\-',.&\\{\\}\\(\\)\\[\\]:;_‐–—"

  if (!allowedSymbols || allowedSymbols.length === 0) {
    return new RegExp(`^[a-zA-ZÀ-ÿß0-9${defaultSymbols}]+$`)
  }

  const escapedSymbols = allowedSymbols
    .map((symbol) => symbol.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
    .join('')

  return new RegExp(`^[a-zA-ZÀ-ÿß0-9${defaultSymbols}${escapedSymbols}]+$`)
}

export const createLatinCharacterSchema = (
  errorMessage: string,
  allowedSymbols?: string[]
) => {
  return z.string().refine(
    (value) => {
      if (!value) {
        return true
      }
      const regex = createLatinCharacterRegex(allowedSymbols)
      return regex.test(value)
    },
    {
      message: errorMessage,
    }
  )
}

export const validateLatinCharacters = (
  value: string,
  errorMessage: string,
  allowedSymbols?: string[]
): string | null => {
  if (!value) {
    return null
  }

  try {
    const schema = createLatinCharacterSchema(errorMessage, allowedSymbols)
    schema.parse(value)
    return null
  } catch (error) {
    if (error instanceof z.ZodError) {
      return error.issues[0]?.message || errorMessage
    }
    return errorMessage
  }
}

export const createRequiredLatinCharacterSchema = (
  errorMessage: string,
  options?: { min?: number; max?: number },
  allowedSymbols?: string[]
) => {
  let schema = z.string().min(1)

  if (options?.min) {
    schema = schema.min(options.min)
  }
  if (options?.max) {
    schema = schema.max(options.max)
  }

  return schema.refine(
    (value) => {
      const regex = createLatinCharacterRegex(allowedSymbols)
      return regex.test(value)
    },
    {
      message: errorMessage,
    }
  )
}

export const createOptionalLatinCharacterSchema = (
  errorMessage: string,
  options?: { max?: number },
  allowedSymbols?: string[]
) => {
  let schema = z.string()
  if (options?.max) {
    schema = schema.max(options.max)
  }
  return schema.refine(
    (value) => {
      if (!value) {
        return true
      }
      const regex = createLatinCharacterRegex(allowedSymbols)
      return regex.test(value)
    },
    {
      message: errorMessage,
    }
  )
}
